/**
 * Web component: Main article (main-article)
 * -----------------------------------------------------------------------------
 * Main article styles and table of contents functionality.
 *
 */
import './main-article.scss';

import { vueReady } from '~/core/runners';
import classes from '~/config/classes';
import { convertToHandle } from '~/helpers/convert';

const selectors = {
  content: '[js-main-article="content"]',
  toc: '[js-main-article="toc"]',
  tocList: '[js-main-article="toc-list"]',
};

/**
 * Create new <main-article> custom element.
 */
class MainArticle extends HTMLElement {
  static observedAttributes = [];

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super();

    cnvs.EventBus.listen('blog-page', (payload) => {
      if (payload.headlines.length > 0) {
        this.headings = [...this.headings, ...payload.headlines];
        this.buildToc();
        this.elements.tocList.innerHTML = this.template;
      }
    });

    this.elements = {
      content: this.querySelector(selectors.content),
      toc: this.querySelector(selectors.toc),
      tocList: this.querySelector(selectors.tocList),
    };

    this.headings = [];
    this.template = '';
  }

  /**
   * Lifecycle hook - component is attached to DOM.
   */
  connectedCallback() {
    this.headings = [
      ...this.elements.content.querySelectorAll('h2, h3, h4, h5, h6'),
    ];
    this.buildToc();
    this.elements.toc.classList.remove(classes.hidden);
    this.elements.tocList.innerHTML = this.template;
  }

  /**
   * Build table of contents.
   */
  buildToc() {
    let previousHeading = 2;
    let openLists = 0;
    this.template = '';

    /**
     * Iterate over heading elements.
     */
    for (const element of this.headings) {
      if (!element.innerText) {
        continue;
      }

      const heading = Number(element.tagName.replace('H', ''));

      /**
       * Determine layer to add heading to.
       * - Except first time where previous heading hasn't been set.
       * - If heading is less than previous reset indices values that follow.
       */

      const diff = previousHeading - heading;

      if (diff < 0) {
        for (let index = 0; index > diff; index--) {
          this.template += '<li>';
          this.template += '<ul>';
          openLists++;
        }
      }
      if (diff > 0) {
        for (let index = 0; index < diff; index++) {
          this.template += '</ul>';
          this.template += '</li>';
          openLists--;
        }
      }
      this.template += `<li>${this.renderAnchor(element)}</li>`;

      previousHeading = heading;
    }

    // If we have opened more <ul> than closed, close them to sanitize HTML.

    for (let index = 0; index < openLists; index++) {
      this.template += '</ul>';
      this.template += '</li>';
    }
  }

  /**
   * Render anchor element.
   * - Also updates element ID.
   * @param {HTMLElement} element - Heading element.
   * @returns {String}
   */
  renderAnchor(element) {
    const title = element.innerText;
    const formattedTitle = title.replace(/^\W\d/g, '').trim();
    const titleHandle = convertToHandle(formattedTitle);
    element.id = titleHandle;

    return `
      <a
        class="
          text-mobile-paragraph-m-regular-link
          text-desktop-paragraph-m-regular-link
        "
        href="#${titleHandle}"
      >
        ${title}
      </a>
    `;
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('main-article')) {
    return;
  }

  customElements.define('main-article', MainArticle);
});
